import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Plus, Trash2, Sparkles, Search, Upload, RotateCcw, ChevronUp, ChevronDown } from "lucide-react";
import { F, TxtArea } from "../_shared";

export default function StepDestination({ q, setQ, onAI, aiIdx, aiBusy }) {
  const DESTINATIONS_CACHE_REF = useRef([]);
  const dests = (q.destinations && q.destinations.length)
    ? q.destinations
    : [q.destination || {}];

  const syncAll = (next) => {
    setQ((p) => ({ ...p, destinations: next, destination: next[0] || {} }));
  };
  const updBlock = (idx, patch) =>
    syncAll(dests.map((d, i) => i === idx ? { ...d, ...patch } : d));
  const addBlock = () =>
    syncAll([...dests, { country: "", country_code: "", flag_url: "", hero_image: "",
                         overview: "", best_time: "", currency: "", weather: "",
                         attractions: "", experiences: "" }]);
  const removeBlock = (idx) => {
    if (dests.length <= 1) return;
    syncAll(dests.filter((_, i) => i !== idx));
  };
  const moveBlock = (idx, delta) => {
    const t = idx + delta;
    if (t < 0 || t >= dests.length) return;
    const next = [...dests];
    [next[idx], next[t]] = [next[t], next[idx]];
    syncAll(next);
  };

  return (
    <>
      {dests.map((d, idx) => (
        <DestinationBlock
          key={idx}
          idx={idx}
          total={dests.length}
          dest={d}
          onPatch={(patch) => updBlock(idx, patch)}
          onRemove={() => removeBlock(idx)}
          onMoveUp={() => moveBlock(idx, -1)}
          onMoveDown={() => moveBlock(idx, +1)}
          onAI={() => onAI(idx)}
          aiBusy={aiBusy && aiIdx === idx}
          cacheRef={DESTINATIONS_CACHE_REF}
        />
      ))}
      <button
        data-testid="dest-add-another"
        onClick={addBlock}
        className="mt-5 w-full border-2 border-dashed border-slate-300 hover:border-emerald-500 hover:bg-emerald-50 text-emerald-700 py-3 rounded flex items-center justify-center gap-2 text-sm font-medium transition-colors"
      >
        <Plus size={14} /> Add Another Country
      </button>
    </>
  );
}

function DestinationBlock({ idx, total, dest, onPatch, onRemove, onMoveUp, onMoveDown, onAI, aiBusy, cacheRef }) {
  const [query, setQuery] = useState(dest.country || "");
  const [suggestions, setSuggestions] = useState([]);
  const [showList, setShowList] = useState(false);

  useEffect(() => { setQuery(dest.country || ""); }, [dest.country]);

  useEffect(() => {
    if (query.length < 1) { setSuggestions([]); return; }
    api.get("/catalog/destinations", { params: { q: query } }).then(({ data }) => {
      setSuggestions(data);
      const seen = new Set(cacheRef.current.map((x) => x.code));
      data.forEach((d) => { if (!seen.has(d.code)) cacheRef.current.push(d); });
    });
  }, [query]);

  const pick = (d) => {
    setQuery(d.country); setShowList(false);
    onPatch({ country: d.country, country_code: d.code, flag_url: d.flag,
              hero_image: d.hero, currency: d.currency });
  };

  const isMulti = total > 1;

  return (
    <div className={isMulti ? "border border-slate-200 rounded p-5 mb-5 bg-slate-50/40" : "mb-2"}>
      {isMulti && (
        <div className="flex items-center justify-between mb-3">
          <div className="text-[11px] uppercase tracking-widest text-emerald-700 font-semibold">
            Country {idx + 1} of {total}
          </div>
          <div className="flex items-center gap-1">
            <button data-testid={`dest-move-up-${idx}`} onClick={onMoveUp} disabled={idx === 0}
                    className="p-1.5 rounded text-slate-500 hover:bg-slate-100 disabled:opacity-30"
                    title="Move up"><ChevronUp size={14} /></button>
            <button data-testid={`dest-move-down-${idx}`} onClick={onMoveDown} disabled={idx === total - 1}
                    className="p-1.5 rounded text-slate-500 hover:bg-slate-100 disabled:opacity-30"
                    title="Move down"><ChevronDown size={14} /></button>
            <button data-testid={`dest-remove-${idx}`} onClick={onRemove}
                    className="p-1.5 rounded text-red-500 hover:bg-red-50"
                    title="Remove country"><Trash2 size={14} /></button>
          </div>
        </div>
      )}
      <div className="relative">
        <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1.5">Search country (type 3+ letters)</label>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-3 text-slate-400" />
          <input data-testid={idx === 0 ? "dest-search" : `dest-search-${idx}`}
                 value={query} onFocus={() => setShowList(true)}
                 onChange={(e) => { setQuery(e.target.value); setShowList(true); }}
                 placeholder="e.g. Tha…"
                 className="w-full pl-9 pr-3 py-2.5 border border-border rounded text-sm bg-white" />
        </div>
        {showList && suggestions.length > 0 && (
          <div className="absolute z-20 top-full left-0 mt-1 w-full bg-white border border-border rounded shadow-lg max-h-72 overflow-auto">
            {suggestions.map((d) => (
              <button key={d.code} data-testid={`dest-opt-${idx}-${d.code}`}
                      onClick={() => pick(d)}
                      className="flex items-center gap-3 w-full text-left px-4 py-2.5 hover:bg-slate-50 text-sm">
                <img src={d.flag} alt="" className="w-8 h-5 object-cover rounded-sm border border-border" />
                <span>{d.country}</span>
                <span className="ml-auto text-xs text-slate-400">{d.code}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {dest.country && (
        <div className="mt-6">
          <div className="relative rounded overflow-hidden h-56 group">
            <img src={dest.hero_image} className="absolute inset-0 w-full h-full object-cover" alt="" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
            <div className="absolute bottom-4 left-5 text-white flex items-center gap-3">
              <img src={dest.flag_url} className="w-10 h-6 border border-white/40 object-cover rounded-sm" alt="" />
              <div className="font-editorial text-3xl">{dest.country}</div>
            </div>
            <label
              data-testid={`dest-hero-upload-btn-${idx}`}
              htmlFor={`dest-hero-upload-input-${idx}`}
              className="absolute top-3 right-3 bg-white/90 hover:bg-white text-slate-800 text-xs px-3 py-1.5 rounded shadow-sm inline-flex items-center gap-1.5 cursor-pointer transition-colors"
              title="Upload your own hero image"
            >
              <Upload size={13} /> Upload image
            </label>
            <input
              id={`dest-hero-upload-input-${idx}`}
              data-testid={`dest-hero-upload-input-${idx}`}
              type="file" accept="image/*" className="hidden"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                if (file.size > 5 * 1024 * 1024) { toast.error("Max file size 5 MB"); e.target.value = ""; return; }
                try {
                  const fd = new FormData(); fd.append("file", file);
                  const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                  const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
                  const abs = data.url.startsWith("http") ? data.url : `${base}${data.url}`;
                  onPatch({ hero_image: abs });
                  toast.success("Hero image updated");
                } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
                finally { e.target.value = ""; }
              }}
            />
            {dest.hero_image && (
              <button
                data-testid={`dest-hero-reset-btn-${idx}`}
                onClick={() => {
                  const orig = (cacheRef.current || []).find((x) => x.code === dest.country_code);
                  if (orig?.hero) { onPatch({ hero_image: orig.hero }); toast.success("Restored auto image"); }
                }}
                className="absolute top-3 right-32 bg-white/90 hover:bg-white text-slate-700 text-xs px-3 py-1.5 rounded shadow-sm inline-flex items-center gap-1.5 transition-colors"
                title="Restore auto-selected image"
              >
                <RotateCcw size={13} /> Auto
              </button>
            )}
          </div>
          <div className="text-[11px] text-slate-500 mt-1.5">
            The hero image is auto-picked for the destination. You can upload your own (max 5 MB) or restore the auto pick anytime.
          </div>

          <button data-testid={`ai-dest-btn-${idx}`} onClick={onAI} disabled={aiBusy}
            className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded border border-[#16A34A] text-[#16A34A] text-sm hover:bg-[#16A34A] hover:text-white transition-colors">
            <Sparkles size={14} /> {aiBusy ? "Generating…" : "AI generate destination info"}
          </button>

          <div className="mt-4 space-y-4">
            <TxtArea label="Overview" value={dest.overview} onChange={(v) => onPatch({ overview: v })} testid={`dest-overview-${idx}`} />
            <div className="grid grid-cols-2 gap-4">
              <F label="Best Time" value={dest.best_time} onChange={(v) => onPatch({ best_time: v })} testid={`dest-best-time-${idx}`} />
              <F label="Currency" value={dest.currency} onChange={(v) => onPatch({ currency: v })} testid={`dest-currency-${idx}`} />
            </div>
            <TxtArea label="Weather" value={dest.weather} onChange={(v) => onPatch({ weather: v })} testid={`dest-weather-${idx}`} />
            <TxtArea label="Famous Attractions" value={dest.attractions} onChange={(v) => onPatch({ attractions: v })} testid={`dest-attractions-${idx}`} />
            <TxtArea label="Local Experiences" value={dest.experiences} onChange={(v) => onPatch({ experiences: v })} testid={`dest-experiences-${idx}`} />
          </div>
        </div>
      )}
    </div>
  );
}
